import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, Play, X, TrendingUp, Target, Users, BarChart } from 'lucide-react';

const MetaAdsStrategy = () => {
  const [popupItem, setPopupItem] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToBooking = () => {
    document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const testimonialVideos = [
    { src: 'https://www.youtube.com/embed/izRXTVJf0pw', id: 'izRXTVJf0pw' },
    { src: 'https://www.youtube.com/embed/ZXouPQGQOn0', id: 'ZXouPQGQOn0' },
    { src: 'https://www.youtube.com/embed/MHHjat7Zjxs', id: 'MHHjat7Zjxs' },
  ];

  const coverageItems = [
    {
      icon: <Target className="w-6 h-6" />,
      title: 'YOUR OFFER',
      desc: "We'll look at what you're selling and whether the offer is strong enough to turn paid traffic into customers."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'YOUR ADS',
      desc: "We'll talk through the type of ads and creative we'd recommend for your business."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'YOUR CAMPAIGN',
      desc: "We'll show you how we'd structure your Meta Ads campaign and who we'd target."
    },
    {
      icon: <BarChart className="w-6 h-6" />,
      title: 'YOUR FOLLOW-UP',
      desc: "We'll look at what happens after someone becomes a lead and how you can turn more of those leads into actual customers."
    }
  ];

  const services = [
    'Meta Ads campaign strategy',
    'Facebook and Instagram ad management',
    'Video ad creative',
    'Static ad creative',
    'Copywriting',
    'Lead forms',
    'Landing pages',
    'CRM setup',
    'Automated lead follow-up',
    'Campaign optimization',
    'Reporting and strategy'
  ];

  const qualifications = [
    'You run an established local business',
    'You want a predictable way to generate more leads',
    "You're already running ads but aren't happy with the results",
    'You rely mostly on referrals and want another source of customers',
    'You want someone to handle the creative AND the advertising',
    "You're willing to invest in marketing if there is a clear strategy behind it"
  ];

  return (
    <div className="min-h-screen relative z-[1] bg-slate-950">
      {/* Simplified Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center space-x-2">
            <span className="text-amber-400 text-2xl">⚡</span>
            <span className="text-white font-black text-xl">PHOS STUDIO</span>
          </a>
          <Button 
            onClick={scrollToBooking}
            className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold px-6 py-2 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all hover:scale-105"
          >
            BOOK YOUR FREE CALL
          </Button>
        </div>
      </header>

      <div className="pt-24 pb-20">
        {/* ===== HERO SECTION ===== */}
        <section className="py-12 md:py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Left Column - Copy */}
                <div>
                  <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
                    Stop Guessing With Your<br />
                    <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Meta Ads.</span>
                  </h1>
                  
                  <p className="text-xl sm:text-2xl text-slate-300 mb-6 leading-relaxed">
                    Book a free strategy call and we'll show you how we'd use Facebook and Instagram ads to generate more qualified leads for your business.
                  </p>

                  <p className="text-lg text-slate-400 mb-10 leading-relaxed">
                    We'll look at your offer, your current marketing, your market, and where we see the biggest opportunity for growth.
                  </p>

                  <Button 
                    onClick={scrollToBooking}
                    size="lg" 
                    className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold text-xl px-12 py-7 shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-105 mb-3"
                  >
                    BOOK YOUR FREE STRATEGY CALL <ArrowRight className="ml-2 w-6 h-6" />
                  </Button>
                  
                  <p className="text-slate-500 text-sm">
                    Free 30-minute call. No obligation.
                  </p>
                </div>

                {/* Right Column - Booking Calendar */}
                <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 shadow-2xl">
                  <h3 className="text-2xl font-bold text-white mb-4">Choose Your Time</h3>
                  <div className="bg-white rounded-lg overflow-hidden" style={{ minHeight: '600px' }}>
                    <iframe 
                      src="https://book.phosstudio.net/widget/booking/kqhj7PgMkZvYoQ1TXjIR" 
                      allow="payment" 
                      style={{ width: '100%', height: '600px', border: 'none', overflow: 'hidden' }} 
                      scrolling="no" 
                      id="eW5I0w0Iqual0x0p4iRA_top"
                      title="Book Meta Ads Strategy Call"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== WHAT WE'LL COVER ===== */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-14">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                  What We'll Break Down <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">On The Call</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {coverageItems.map((item, i) => (
                  <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 hover:border-amber-500/30 transition-all">
                    <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 mb-5">
                      {item.icon}
                    </div>
                    <h3 className="text-white font-bold text-xl mb-3">{item.title}</h3>
                    <p className="text-slate-400 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== DIFFERENTIATOR ===== */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-8">
                We Don't Care About <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Vanity Metrics.</span>
              </h2>
              
              <p className="text-xl text-slate-300 mb-12 leading-relaxed max-w-3xl mx-auto">
                Impressions, views, clicks and engagement are great, but they don't mean much if they aren't helping your business make money.
              </p>

              <p className="text-xl text-slate-300 mb-12 leading-relaxed">
                Our goal is to build campaigns around <span className="text-amber-400 font-bold">actual business results.</span>
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-12">
                <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8">
                  <p className="text-slate-500 text-sm uppercase font-bold mb-2">Nice To Have</p>
                  <p className="text-4xl font-black text-slate-600 mb-2">100,000</p>
                  <p className="text-slate-500">Impressions</p>
                </div>
                <div className="bg-gradient-to-br from-amber-500/20 to-yellow-400/20 border border-amber-500/50 rounded-2xl p-8">
                  <p className="text-amber-400 text-sm uppercase font-bold mb-2">What Matters</p>
                  <p className="text-4xl font-black text-white mb-2">Actual</p>
                  <p className="text-amber-400 font-bold">Customers Generated</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                {['More qualified leads', 'More booked appointments', 'More customers', 'More revenue'].map((item, i) => (
                  <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                    <Check className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                    <p className="text-slate-300 text-sm font-medium">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== CLIENT TESTIMONIALS ===== */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-14">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                  Don't Take Our <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Word For It.</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {testimonialVideos.map((video, index) => (
                  <div
                    key={index}
                    className="cursor-pointer group"
                    onClick={() => setPopupItem({ src: video.src, aspect: 'portrait', type: 'youtube' })}
                  >
                    <div className="relative aspect-[9/16] bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/10">
                      <img
                        src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                        alt={`Client testimonial ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-slate-950/30 group-hover:bg-slate-950/10 transition-all flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                          <Play className="w-8 h-8 text-slate-950 ml-0.5" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== WHAT PHOS CAN HANDLE ===== */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-14">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-6">
                  More Than Just <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Running Ads</span>
                </h2>
                <p className="text-xl text-slate-300 leading-relaxed">
                  If it makes sense for your business, PHOS can handle the entire system needed to run your campaign.
                </p>
              </div>

              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 md:p-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services.map((service, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <Check className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300 text-lg">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== WHO THIS IS FOR ===== */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-14">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                  This Call Is <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">For You If...</span>
                </h2>
              </div>

              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 md:p-12 mb-8">
                <div className="space-y-4">
                  {qualifications.map((qual, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-4 h-4 text-amber-400" />
                      </div>
                      <span className="text-slate-300 text-lg leading-relaxed">{qual}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <p className="text-slate-400 leading-relaxed">
                  <span className="font-bold text-white">This probably isn't the right fit</span> if you're looking for overnight results, aren't ready to invest in advertising, or are only looking for the cheapest possible marketing option.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== BOOKING SECTION ===== */}
        <section id="booking-section" className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                  Let's Build Your <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Game Plan.</span>
                </h2>
                <p className="text-xl text-slate-400">
                  Choose a time below for your free Meta Ads Strategy Call.
                </p>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {/* Left - What We'll Discuss */}
                <div className="lg:col-span-1">
                  <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 sticky top-24">
                    <h3 className="text-xl font-bold text-white mb-6">During this 30-minute call we'll discuss:</h3>
                    <div className="space-y-3">
                      {[
                        'Your business and services',
                        'Your current lead generation',
                        'Your ideal customer',
                        'Your advertising goals',
                        'Your potential ad budget',
                        "The campaign we'd recommend",
                        'Whether PHOS is a good fit to help you execute it'
                      ].map((item, i) => (
                        <div key={i} className="flex items-start space-x-3">
                          <Check className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-300">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right - Calendar */}
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: '700px' }}>
                    <iframe 
                      src="https://book.phosstudio.net/widget/booking/kqhj7PgMkZvYoQ1TXjIR" 
                      allow="payment" 
                      style={{ width: '100%', height: '700px', border: 'none', overflow: 'hidden' }} 
                      scrolling="no" 
                      id="eW5I0w0Iqual0x0p4iRA_bottom"
                      title="Book Meta Ads Strategy Call"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Video Popup Modal */}
      {popupItem && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4"
          onClick={() => setPopupItem(null)}
        >
          <div
            className="relative max-w-4xl w-full bg-slate-900 rounded-2xl border border-amber-500/30 shadow-2xl shadow-amber-500/10 overflow-hidden mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPopupItem(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-slate-800/80 text-white hover:bg-amber-500/20 hover:text-amber-400 flex items-center justify-center transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-full aspect-[9/16] max-h-[80vh]">
              <iframe
                src={popupItem.src}
                className="w-full h-full border-0"
                title="Client Testimonial"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <script src="https://book.phosstudio.net/js/form_embed.js" async />
    </div>
  );
};

export default MetaAdsStrategy;
